import React, { useContext, useMemo } from "react";
import classNames from "classnames";
import "./ChangeHighlight.scss";

export interface ChangeHighlightProps {
  changed?: boolean;
  onUndo?: () => void;
  onKeep?: () => void;
  children: React.ReactNode;
  changePath?: string;
  hideActions?: boolean;
}

interface ChangeHighlightContextValue {
  totalChanges: number;
  getChangeIndex?: (path?: string | null) => number | undefined;
  dimUnchanged?: boolean;
  hideActions?: boolean;
}

export const ChangeHighlightContext = React.createContext<ChangeHighlightContextValue>({
  totalChanges: 0,
  getChangeIndex: undefined,
  dimUnchanged: false,
  hideActions: false,
});

export interface ChangeHighlightProviderProps {
  children: React.ReactNode;
  changeIndexMap?: Record<string, number>;
  totalChanges?: number;
  dimUnchanged?: boolean;
  hideActions?: boolean;
}

export const ChangeHighlightProvider = ({
  children,
  changeIndexMap,
  totalChanges = 0,
  dimUnchanged = false,
  hideActions = false,
}: ChangeHighlightProviderProps) => {
  const value = useMemo(
    () => ({
      totalChanges,
      getChangeIndex: changeIndexMap ? ((path?: string | null) => (path ? changeIndexMap[path] : undefined)) : undefined,
      dimUnchanged,
      hideActions,
    }),
    [changeIndexMap, dimUnchanged, hideActions, totalChanges]
  );
  return <ChangeHighlightContext.Provider value={value}>{children}</ChangeHighlightContext.Provider>;
};

const ChangeHighlight = ({ changed, onUndo, onKeep, children, changePath, hideActions }: ChangeHighlightProps) => {
  const { totalChanges, getChangeIndex, dimUnchanged, hideActions: ctxHideActions } = useContext(ChangeHighlightContext);
  const showActions = !(ctxHideActions || hideActions) && Boolean(changed && (onUndo || onKeep));
  const changeIndex = changed && changePath && getChangeIndex ? getChangeIndex(changePath) : undefined;
  const showCounter = showActions && typeof changeIndex === "number" && totalChanges > 0;
  const isDimmed = dimUnchanged && !changed;
  return (
    <div
      className={classNames("change-highlight", {
        "change-highlight--changed": changed,
        "change-highlight--with-actions": showActions,
        "change-highlight--dimmed": isDimmed,
      })}
    >
      <div className="change-highlight__content">{children}</div>
      {showActions && (
        <div className="change-highlight__actions">
          <div className="change-highlight__actions-outer">
            <div className="change-highlight__actions-inner">
              {onKeep && (
                <button
                  type="button"
                  onClick={onKeep}
                  className="change-highlight__action change-highlight__action--keep"
                >
                  Keep
                </button>
              )}
              {onUndo && (
                <button
                  type="button"
                  onClick={onUndo}
                  className="change-highlight__action change-highlight__action--undo"
                >
                  Undo
                </button>
              )}
              {showCounter && (
                <>
                  <span className="change-highlight__divider" aria-hidden="true" />
                  <span className="change-highlight__counter">
                    {changeIndex} of {totalChanges}
                  </span>
                  {/* <div className="change-highlight__nav" aria-hidden="true">
                    <span className="change-highlight__nav-arrow change-highlight__nav-arrow--up">↑</span>
                    <span className="change-highlight__nav-arrow change-highlight__nav-arrow--down">↓</span>
                  </div> */}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChangeHighlight;
